import React, { useEffect, useMemo, useState } from 'react';
import axios from '../config/api';
import { useToast } from '../context/ToastContext';
import { formatCurrency } from '../utils/format';
import {
  ISSUE_REPORT_WINDOW_HOURS,
  ISSUE_STATUS_LABELS,
  ORDER_ISSUE_TYPES,
  RESOLUTION_OPTIONS,
  OrderIssueTypeId,
  getIssueTypeLabel,
  getResolutionLabel,
  getRefundOutcomeLabel,
  isWithinIssueReportWindow,
} from '../content/orderIssueTypes';
import { HELP_CONTACT } from '../content/helpContent';

interface OrderItem {
  menu_item_id: string;
  quantity: number;
  name?: string;
}

interface OrderSummary {
  id: string;
  order_number?: string;
  cafe_name: string;
  total_amount: number;
  payment_method?: string;
  created_at: string;
  status?: string;
  issue_id?: string | null;
  items: OrderItem[];
}

interface RefundBreakdownLine {
  menu_item_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface RefundEstimate {
  estimated_refund_amount: number;
  refund_breakdown: RefundBreakdownLine[];
  is_entire_order?: boolean;
}

interface ExistingIssue {
  id: string;
  issue_type: string;
  status: string;
  resolution_preference?: string;
  refund_outcome?: string;
  estimated_refund_amount?: number;
  refund_breakdown?: RefundBreakdownLine[];
  created_at: string;
}

interface ResolutionOption {
  id: string;
  label: string;
}

interface ReportOrderIssueModalProps {
  order: OrderSummary;
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

const MAX_PHOTOS = 3;

function formatRefundLabelWithAmount(refundOutcome: string, amount?: number): string {
  const baseLabel = getRefundOutcomeLabel(refundOutcome);
  const showAmount = (refundOutcome === 'full' || refundOutcome === 'partial') && (amount ?? 0) > 0;
  return showAmount ? `${baseLabel} (${formatCurrency(amount || 0)})` : baseLabel;
}

function RefundEstimateBox({
  estimate,
  loading,
}: {
  estimate: RefundEstimate | null;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <p className="text-sm text-gray-500 rounded-lg border border-dashed border-gray-200 p-3">
        Calculating estimated refund…
      </p>
    );
  }
  if (!estimate || estimate.refund_breakdown.length === 0) {
    return null;
  }
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm space-y-2">
      <p className="font-semibold text-amber-900 dark:text-amber-100">Estimated refund</p>
      <ul className="space-y-1 text-gray-800 dark:text-gray-200">
        {estimate.refund_breakdown.map((row) => (
          <li key={row.menu_item_id} className="flex justify-between gap-2">
            <span>
              {row.quantity}× {row.name}
            </span>
            <span className="font-medium shrink-0">{formatCurrency(row.line_total)}</span>
          </li>
        ))}
      </ul>
      <p className="flex justify-between border-t border-amber-200 pt-2 font-bold text-amber-900 dark:text-amber-100">
        <span>Total estimated refund</span>
        <span>{formatCurrency(estimate.estimated_refund_amount)}</span>
      </p>
      <p className="text-xs text-gray-600 dark:text-gray-400">
        Final amount is confirmed after our team reviews your report.
      </p>
    </div>
  );
}

export default function ReportOrderIssueModal({
  order,
  isOpen,
  onClose,
  onSubmitted,
}: ReportOrderIssueModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [canReport, setCanReport] = useState(false);
  const [existingIssue, setExistingIssue] = useState<ExistingIssue | null>(null);
  const [statusLoadFailed, setStatusLoadFailed] = useState(false);

  const [issueType, setIssueType] = useState<OrderIssueTypeId | ''>('');
  const [affectedItems, setAffectedItems] = useState<Set<string>>(new Set());
  const [entireOrder, setEntireOrder] = useState(false);
  const [description, setDescription] = useState('');
  const [resolution, setResolution] = useState('review');
  const [confirmed, setConfirmed] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [refundEstimate, setRefundEstimate] = useState<RefundEstimate | null>(null);
  const [refundEstimateLoading, setRefundEstimateLoading] = useState(false);
  const [resolutionOptions, setResolutionOptions] = useState<ResolutionOption[]>(RESOLUTION_OPTIONS as unknown as ResolutionOption[]);
  const isCashOrder = String(order.payment_method || '').toLowerCase() === 'cash';

  const selectedType = ORDER_ISSUE_TYPES.find((t) => t.id === issueType);
  const needsItems = selectedType?.needsItems ?? false;

  const lineItems = useMemo(() => {
    const m = new Map<string, { menu_item_id: string; name: string; quantity: number }>();
    for (const it of order.items) {
      const prev = m.get(it.menu_item_id);
      const label = it.name?.trim() || 'Item';
      if (prev) prev.quantity += it.quantity;
      else m.set(it.menu_item_id, { menu_item_id: it.menu_item_id, name: label, quantity: it.quantity });
    }
    return Array.from(m.values());
  }, [order.items]);

  useEffect(() => {
    if (!isOpen) return;
    document.documentElement.classList.add('report-issue-modal-open');
    return () => {
      document.documentElement.classList.remove('report-issue-modal-open');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setIssueType('');
    setAffectedItems(new Set());
    setEntireOrder(false);
    setDescription('');
    setResolution(isCashOrder ? 'refund_points' : 'refund_card');
    setConfirmed(false);
    setPhotos([]);
    setPhotoPreviews([]);
    setRefundEstimate(null);
    setStatusLoadFailed(false);
    setResolutionOptions(RESOLUTION_OPTIONS as unknown as ResolutionOption[]);

    const applyLocalFallback = () => {
      setStatusLoadFailed(true);
      const localOptions =
        isCashOrder
          ? (RESOLUTION_OPTIONS as unknown as ResolutionOption[]).filter((opt) => opt.id === 'refund_points')
          : (RESOLUTION_OPTIONS as unknown as ResolutionOption[]);
      setResolutionOptions(localOptions);
      setResolution(localOptions[0]?.id || 'refund_card');
      if (order.issue_id) {
        setExistingIssue({
          id: order.issue_id,
          issue_type: 'unknown',
          status: 'submitted',
          created_at: order.created_at,
        });
        setCanReport(false);
        return;
      }
      const completed = order.status === 'completed';
      const inWindow = isWithinIssueReportWindow(order.created_at);
      setCanReport(completed && inWindow);
      setExistingIssue(null);
    };

    axios
      .get(`/api/orders/${order.id}/issue-status`)
      .then((res) => {
        const remoteOptions: ResolutionOption[] =
          Array.isArray(res.data.resolution_options) && res.data.resolution_options.length > 0
            ? res.data.resolution_options
            : (RESOLUTION_OPTIONS as unknown as ResolutionOption[]);
        setResolutionOptions(remoteOptions);
        const preferred = isCashOrder ? 'refund_points' : 'refund_card';
        setResolution(remoteOptions.some((opt) => opt.id === preferred) ? preferred : (remoteOptions[0]?.id || 'refund_card'));
        setCanReport(!!res.data.can_report);
        setExistingIssue(res.data.issue || null);
        setStatusLoadFailed(false);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 404 || status === 501 || !err.response) {
          applyLocalFallback();
        } else {
          showToast(
            err.response?.data?.error || 'Could not load issue status',
            'error'
          );
          applyLocalFallback();
        }
      })
      .finally(() => setLoading(false));
  }, [isOpen, order.id, order.status, order.issue_id, order.created_at, order.payment_method, showToast, isCashOrder]);

  useEffect(() => {
    if (!resolutionOptions.some((opt) => opt.id === resolution)) {
      setResolution(resolutionOptions[0]?.id || '');
    }
  }, [resolutionOptions, resolution]);

  const affectedPayload = useMemo(() => {
    if (!needsItems || !issueType) return null;
    if (entireOrder) {
      return lineItems.map((l) => ({
        menu_item_id: l.menu_item_id,
        quantity: l.quantity,
        entire_order: true,
      }));
    }
    if (affectedItems.size === 0) return null;
    return lineItems
      .filter((l) => affectedItems.has(l.menu_item_id))
      .map((l) => ({ menu_item_id: l.menu_item_id, quantity: l.quantity }));
  }, [needsItems, issueType, entireOrder, lineItems, affectedItems]);

  useEffect(() => {
    if (!isOpen || !canReport || existingIssue || loading) {
      setRefundEstimate(null);
      return;
    }
    if (!needsItems || !issueType) {
      setRefundEstimate(null);
      return;
    }
    if (!affectedPayload) {
      setRefundEstimate(null);
      return;
    }

    const timer = window.setTimeout(() => {
      setRefundEstimateLoading(true);
      axios
        .post(`/api/orders/${order.id}/estimate-issue-refund`, {
          affected_items: affectedPayload,
        })
        .then((res) => setRefundEstimate(res.data))
        .catch(() => setRefundEstimate(null))
        .finally(() => setRefundEstimateLoading(false));
    }, 350);

    return () => window.clearTimeout(timer);
  }, [isOpen, canReport, existingIssue, loading, order.id, needsItems, issueType, affectedPayload]);

  useEffect(() => {
    return () => {
      photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [photoPreviews]);

  const toggleItem = (menuItemId: string) => {
    setEntireOrder(false);
    setAffectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(menuItemId)) next.delete(menuItemId);
      else next.add(menuItemId);
      return next;
    });
  };

  const onPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    const combined = [...photos, ...picked].slice(0, MAX_PHOTOS);
    photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    setPhotos(combined);
    setPhotoPreviews(combined.map((f) => URL.createObjectURL(f)));
    e.target.value = '';
  };

  const removePhoto = (index: number) => {
    const next = photos.filter((_, i) => i !== index);
    photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    setPhotos(next);
    setPhotoPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!issueType) {
      showToast('Select what went wrong', 'error');
      return;
    }
    if (needsItems && !entireOrder && affectedItems.size === 0) {
      showToast('Select affected items or entire order', 'error');
      return;
    }
    if (photos.length < 1) {
      showToast('Add at least one photo', 'error');
      return;
    }
    if (!confirmed) {
      showToast('Please confirm your report is accurate', 'error');
      return;
    }
    if (isCashOrder && resolution === 'refund_card') {
      showToast('Cash orders can only be refunded as Qahwahub credit', 'error');
      return;
    }

    const itemsPayload = entireOrder
      ? lineItems.map((l) => ({ menu_item_id: l.menu_item_id, quantity: l.quantity, entire_order: true }))
      : lineItems
          .filter((l) => affectedItems.has(l.menu_item_id))
          .map((l) => ({ menu_item_id: l.menu_item_id, quantity: l.quantity }));

    const form = new FormData();
    form.append('issue_type', issueType);
    form.append('description', description.trim());
    form.append('resolution_preference', resolution);
    form.append('confirmed', 'true');
    form.append('affected_items', JSON.stringify(itemsPayload));
    photos.forEach((file) => form.append('photos', file));

    setSubmitting(true);
    try {
      const res = await axios.post(`/api/orders/${order.id}/report-issue`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(res.data.message || 'Report submitted', 'success');
      onSubmitted();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to submit report';
      if (err.response?.status === 404) {
        showToast(
          'Report could not be sent — the server needs the latest update. Try again after the API is redeployed.',
          'error'
        );
      } else {
        showToast(msg, 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div
        className="bg-white dark:bg-gray-800 w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl shadow-xl max-h-[92vh] flex flex-col min-h-0"
        role="dialog"
        aria-labelledby="report-issue-title"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 id="report-issue-title" className="text-lg font-bold text-amber-800 dark:text-amber-200">
            Report an issue
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none p-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-sheet-scroll flex-1 min-h-0 px-4 py-4 space-y-4">
          <div className="text-sm bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 space-y-1">
            <p className="font-semibold text-gray-900 dark:text-gray-100">{order.cafe_name}</p>
            {order.order_number && (
              <p className="text-gray-600 dark:text-gray-400">Order: {order.order_number}</p>
            )}
            <p className="text-gray-600 dark:text-gray-400">
              {formatCurrency(order.total_amount)}
              {order.payment_method ? ` · ${order.payment_method}` : ''}
            </p>
            <p className="text-xs text-gray-500">
              Report within {ISSUE_REPORT_WINDOW_HOURS} hours of pickup
            </p>
          </div>

          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading…</p>
          ) : existingIssue ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm space-y-3">
              <p className="font-semibold text-amber-900 dark:text-amber-100">Your report</p>
              <dl className="space-y-2 text-gray-800 dark:text-gray-200">
                <div>
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">Status</dt>
                  <dd className="font-medium text-amber-900 dark:text-amber-100">
                    {ISSUE_STATUS_LABELS[existingIssue.status] || existingIssue.status}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">Issue</dt>
                  <dd>{getIssueTypeLabel(existingIssue.issue_type)}</dd>
                </div>
                {existingIssue.resolution_preference && (
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wide">You asked for</dt>
                    <dd>{getResolutionLabel(existingIssue.resolution_preference)}</dd>
                  </div>
                )}
                {existingIssue.refund_outcome && existingIssue.refund_outcome !== 'pending' && (
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wide">Refund decision</dt>
                    <dd>
                      {formatRefundLabelWithAmount(
                        existingIssue.refund_outcome,
                        existingIssue.estimated_refund_amount
                      )}
                    </dd>
                  </div>
                )}
                {(existingIssue.refund_breakdown?.length ?? 0) > 0 && (
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wide mb-1">Estimated refund</dt>
                    <dd>
                      <RefundEstimateBox
                        estimate={{
                          estimated_refund_amount: existingIssue.estimated_refund_amount ?? 0,
                          refund_breakdown: existingIssue.refund_breakdown || [],
                        }}
                      />
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">Submitted</dt>
                  <dd>{new Date(existingIssue.created_at).toLocaleString()}</dd>
                </div>
                {order.order_number && (
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wide">Reference</dt>
                    <dd className="font-mono text-xs">{order.order_number}</dd>
                  </div>
                )}
              </dl>
              <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed">
                Reviews usually take 24–48 hours. Pull down on My Orders to refresh status. When we
                change your case, the status above updates.
              </p>
              <a
                href={`mailto:${HELP_CONTACT.email}?subject=${encodeURIComponent(
                  `Order issue ${order.order_number || order.id}`
                )}`}
                className="inline-block text-sm font-semibold text-amber-800 dark:text-amber-200 underline"
              >
                Email {HELP_CONTACT.email}
              </a>
            </div>
          ) : !canReport ? (
            <p className="text-sm text-gray-600 py-4">
              This order is no longer eligible for online reporting (must be completed within the
              last {ISSUE_REPORT_WINDOW_HOURS} hours and not already reported).
            </p>
          ) : (
            <form id="report-issue-form" onSubmit={handleSubmit} className="space-y-4">
              {statusLoadFailed && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  Could not reach the issue service. You can still fill the form; if submit fails,
                  the API on Render may need a redeploy with the latest code.
                </p>
              )}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  What went wrong? *
                </label>
                <div className="space-y-2">
                  {ORDER_ISSUE_TYPES.map((t) => (
                    <label
                      key={t.id}
                      className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${
                        issueType === t.id
                          ? 'border-amber-600 bg-amber-50/80'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="issue_type"
                        value={t.id}
                        checked={issueType === t.id}
                        onChange={() => setIssueType(t.id)}
                        className="mt-1"
                      />
                      <span className="text-sm">{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {needsItems && issueType && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Which items? *
                  </label>
                  <label className="flex items-center gap-2 mb-2 text-sm">
                    <input
                      type="checkbox"
                      checked={entireOrder}
                      onChange={(e) => {
                        setEntireOrder(e.target.checked);
                        if (e.target.checked) setAffectedItems(new Set());
                      }}
                    />
                    Entire order
                  </label>
                  {!entireOrder && (
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                      {lineItems.map((line) => (
                        <label key={line.menu_item_id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={affectedItems.has(line.menu_item_id)}
                            onChange={() => toggleItem(line.menu_item_id)}
                          />
                          {line.quantity}× {line.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {needsItems && issueType && (
                <RefundEstimateBox estimate={refundEstimate} loading={refundEstimateLoading} />
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">
                  Describe the problem *
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  required
                  minLength={10}
                  placeholder="Tell us what happened…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Photos * (1–{MAX_PHOTOS})
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Show what you received. Include the receipt or order number in one photo if you can.
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {photoPreviews.map((src, i) => (
                    <div key={src} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute top-0 right-0 bg-black/60 text-white text-xs px-1.5"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {photos.length < MAX_PHOTOS && (
                    <label className="w-20 h-20 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer text-2xl text-gray-400">
                      +
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={onPhotoChange}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Preferred resolution
                </label>
                <div className="space-y-2">
                  {(RESOLUTION_OPTIONS as unknown as ResolutionOption[]).map((o) => {
                    const disabled = isCashOrder && o.id === 'refund_card';
                    return (
                      <label
                        key={o.id}
                        className={`flex items-center p-3 border-2 rounded-lg transition ${
                          disabled
                            ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                            : 'cursor-pointer hover:bg-amber-50/80'
                        }`}
                        style={{ borderColor: !disabled && resolution === o.id ? '#b45309' : undefined }}
                      >
                        <input
                          type="radio"
                          name="resolution"
                          value={o.id}
                          checked={resolution === o.id}
                          onChange={() => setResolution(o.id)}
                          disabled={disabled}
                          className="mr-3 h-4 w-4 shrink-0"
                        />
                        <div className="flex-1">
                          <span className="font-semibold">{o.label}</span>
                          {disabled && (
                            <p className="text-xs mt-1">Unavailable for cash-paid orders</p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  I confirm this information is accurate. False claims may affect my account. Refunds
                  are reviewed within 24–48 hours; card refunds may take 3–7 business days.
                </span>
              </label>
            </form>
          )}
        </div>

        <div className="modal-sheet-footer shrink-0 px-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex gap-2 bg-white dark:bg-gray-800 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-300 font-semibold text-gray-700"
          >
            Close
          </button>
          {canReport && !existingIssue && !loading && (
            <button
              type="submit"
              form="report-issue-form"
              disabled={submitting}
              className="flex-1 py-2.5 rounded-lg bg-amber-700 text-white font-semibold disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit report'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
